import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ScrollReveal from '../components/ScrollReveal';
import AnimatedBackground from '../components/AnimatedBackground';
import { images } from '../data/images';
import {
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Clock,
  Globe,
  MessageCircle,
  Target,
  Brain,
  Plug,
  Languages,
  Mic,
  Sparkles,
  Shield,
  UserPlus,
  TrendingUp,
  Lock,
  Server,
  Scale,
} from 'lucide-react';

const AiConciergePage = () => {
  const { t } = useLanguage();
  const problemSectionRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = t('aiConcierge.meta.title');
    return () => { document.title = prevTitle; };
  }, [t]);

  const scrollToProblem = () => {
    problemSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const capabilityIcons = [
    Brain,
    Plug,
    Languages,
    Mic,
    Sparkles,
    Shield,
    UserPlus,
    TrendingUp,
  ];

  return (
    <div className="min-h-screen">
      {/* SECTION 1: Hero */}
      <section className="relative min-h-[90vh] flex items-center justify-center bg-[#1E2A38] overflow-hidden pt-24">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-[20s] ease-linear"
          style={{
            backgroundImage: `url(${images.hero?.background || ''})`,
            filter: 'brightness(0.3) saturate(0.8)',
          }}
        />
        <AnimatedBackground />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1E2A38]/80 via-transparent to-[#1E2A38]/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2B3A4D]/50 via-transparent to-[#0066CC]/10" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 text-center">
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full glass-blue border border-[#0066CC]/30 mb-8 transition-all duration-700 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <span className="text-[#06B6D4] text-sm font-medium">
              {t('aiConcierge.hero.badge')}
            </span>
          </div>

          <h1
            className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2 tracking-tight transition-all duration-700 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '300ms' }}
          >
            {t('aiConcierge.hero.headline')}
          </h1>
          <p
            className={`text-lg md:text-xl text-[#0066CC] font-medium mb-8 tracking-wide transition-all duration-700 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '400ms' }}
          >
            {t('aiConcierge.hero.subheadline')}
          </p>
          <p
            className={`text-white/70 text-lg max-w-3xl mx-auto mb-10 leading-relaxed transition-all duration-700 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ transitionDelay: '500ms' }}
          >
            {t('aiConcierge.hero.description')}
          </p>

          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 transition-all duration-700 ${
              isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '600ms' }}
          >
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#0066CC] hover:bg-[#0055AA] text-white px-8 py-6 text-base group"
              >
                {t('aiConcierge.hero.ctaDemo')}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              onClick={scrollToProblem}
              className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base"
            >
              {t('aiConcierge.hero.ctaLearn')}
            </Button>
          </div>

          {/* Key stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {t('aiConcierge.hero.stats').map((stat, index) => {
              const icons = [Clock, Globe, MessageCircle, Target];
              const Icon = icons[index];
              return (
                <ScrollReveal key={index} delay={700 + index * 80}>
                  <div className="bg-[#2B3A4D]/80 backdrop-blur-sm rounded-xl p-4 border border-white/10 transition-all duration-300 hover:border-[#06B6D4]/30 hover:scale-[1.02]">
                    <Icon className="w-6 h-6 text-[#06B6D4] mx-auto mb-2" />
                    <div className="text-2xl font-bold text-[#06B6D4] mb-1">
                      {stat.value}
                    </div>
                    <div className="text-white/70 text-sm">{stat.label}</div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 2: The Problem */}
      <section
        ref={problemSectionRef}
        className="relative py-24 bg-[#F8F9FA] scroll-mt-24 overflow-hidden"
      >
        {images.aiConcierge?.problem && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-[0.04]"
            style={{ backgroundImage: `url(${images.aiConcierge.problem})`, filter: 'grayscale(1)' }}
          />
        )}
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2B3A4D] mb-3 text-center">
              {t('aiConcierge.problem.title')}
            </h2>
            <p className="text-[#6B7B8C] text-lg text-center mb-14">
              {t('aiConcierge.problem.subtitle')}
            </p>
          </ScrollReveal>

          <div className="space-y-12">
            {t('aiConcierge.problem.items').map((item, index) => (
              <ScrollReveal key={index} delay={index * 60}>
                <Card className="group bg-white border border-[#E5E7EB] shadow-sm overflow-hidden transition-all duration-300 hover:shadow-lg hover:border-[#0066CC]/20 perspective-container card-3d">
                  <div className="p-6 md:p-8">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#2B3A4D]/5 flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110">
                        <AlertTriangle className="w-5 h-5 text-[#0066CC]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-lg font-semibold text-[#2B3A4D] mb-3">
                          {item.problemTitle}
                        </h3>
                        <p className="text-[#6B7B8C] leading-relaxed text-[15px] mb-5">
                          {item.problem}
                        </p>
                        <div className="pt-4 border-t border-[#E5E7EB]">
                          <p className="text-[#2B3A4D] leading-relaxed text-[15px]">
                            <span className="font-semibold text-[#0066CC]">
                              AI Concierge:{' '}
                            </span>
                            {item.solution}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: Before vs After */}
      <section className="relative py-24 bg-white overflow-hidden">
        {images.aiConcierge?.beforeAfter && (
          <div
            className="absolute right-0 top-1/2 -translate-y-1/2 w-[40%] max-w-md h-full bg-cover bg-center opacity-[0.06] hidden lg:block"
            style={{ backgroundImage: `url(${images.aiConcierge.beforeAfter})`, filter: 'grayscale(1)' }}
          />
        )}
        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2B3A4D] mb-12 text-center">
              {t('aiConcierge.beforeAfter.title')}
            </h2>
          </ScrollReveal>

          <div className="space-y-6">
            {t('aiConcierge.beforeAfter.rows').map((row, index) => (
              <ScrollReveal key={index} delay={index * 60}>
                <Card className="bg-white border border-[#E5E7EB] shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:border-[#0066CC]/15">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                    <div className="p-5 lg:p-6 bg-[#F8F9FA] border-b lg:border-b-0 lg:border-r border-[#E5E7EB]">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7B8C] mb-1">Situation</p>
                      <p className="font-medium text-[#2B3A4D] text-[15px] leading-snug">{row.situation}</p>
                    </div>
                    <div className="p-5 lg:p-6 bg-[#2B3A4D]/[0.03] border-b lg:border-b-0 lg:border-r border-[#E5E7EB]">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7B8C] mb-1">Without AI</p>
                      <p className="text-[#5C5C5C] text-sm leading-relaxed">{row.without}</p>
                    </div>
                    <div className="p-5 lg:p-6 bg-[#0066CC]/[0.04]">
                      <p className="text-xs font-semibold uppercase tracking-wider text-[#0066CC] mb-1">With AI</p>
                      <p className="text-[#2B3A4D] text-sm leading-relaxed">{row.with}</p>
                    </div>
                  </div>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: How It Works */}
      <section className="relative py-24 bg-[#F8F9FA] overflow-hidden">
        {images.aiConcierge?.howItWorks && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-[0.04]"
            style={{ backgroundImage: `url(${images.aiConcierge.howItWorks})`, filter: 'grayscale(1)' }}
          />
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2B3A4D] mb-4 text-center">
              {t('aiConcierge.howItWorks.title')}
            </h2>
            <p className="text-[#6B7B8C] text-lg text-center max-w-2xl mx-auto mb-16">
              {t('aiConcierge.howItWorks.subtitle')}
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t('aiConcierge.howItWorks.cards').map((card, index) => {
              const Icon = capabilityIcons[index];
              return (
                <ScrollReveal key={index} delay={index * 80}>
                  <Card className="bg-white border-0 shadow-sm hover:shadow-xl hover:border-[#0066CC]/20 transition-all duration-300 h-full perspective-container card-3d">
                    <CardHeader>
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                        style={{ backgroundColor: '#0066CC15' }}
                      >
                        <Icon className="w-6 h-6 text-[#0066CC]" />
                      </div>
                      <CardTitle className="text-lg text-[#2B3A4D]">
                        {card.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-[#6B7B8C] text-sm leading-relaxed">
                        {card.description}
                      </p>
                    </CardContent>
                  </Card>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 5: Live Examples */}
      <section className="relative py-24 bg-white overflow-hidden">
        {images.aiConcierge?.integration && (
          <div
            className="absolute left-0 top-0 w-1/3 h-full bg-cover bg-center opacity-[0.05] hidden lg:block"
            style={{ backgroundImage: `url(${images.aiConcierge.integration})`, filter: 'grayscale(1)' }}
          />
        )}
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2B3A4D] mb-4 text-center">
              {t('aiConcierge.liveExamples.title')}
            </h2>
            <p className="text-[#6B7B8C] text-lg text-center mb-12">
              {t('aiConcierge.liveExamples.subtitle')}
            </p>
          </ScrollReveal>

          <Tabs defaultValue="0" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 h-auto gap-2 bg-[#F8F9FA] p-2">
              {t('aiConcierge.liveExamples.scenarioTitles').map((title, i) => (
                <TabsTrigger
                  key={i}
                  value={String(i)}
                  className="data-[state=active]:bg-white data-[state=active]:shadow data-[state=active]:text-[#0066CC]"
                >
                  {title}
                </TabsTrigger>
              ))}
            </TabsList>

            {t('aiConcierge.liveExamples.scenarios').map((scenario, idx) => (
              <TabsContent key={idx} value={String(idx)} className="mt-6">
                <ScrollReveal>
                  <div className="rounded-2xl border border-[#E5E7EB] bg-[#F8F9FA] p-6 space-y-4 min-h-[280px] transition-all duration-300 hover:border-[#0066CC]/20 hover:shadow-md">
                    {scenario.note && (
                      <p className="text-sm text-[#6B7B8C] italic mb-4">
                        {scenario.note}
                      </p>
                    )}
                    {scenario.guest1 && (
                      <div className="flex justify-end">
                        <div className="max-w-[85%] rounded-2xl bg-[#0066CC] text-white px-4 py-3 text-sm">
                          {scenario.guest1}
                        </div>
                      </div>
                    )}
                    {scenario.ai1 && (
                      <div className="flex justify-start">
                        <div className="max-w-[85%] rounded-2xl bg-white border border-[#E5E7EB] px-4 py-3 text-sm text-[#2B3A4D]">
                          {scenario.ai1}
                        </div>
                      </div>
                    )}
                    {scenario.guest2 && (
                      <div className="flex justify-end">
                        <div className="max-w-[85%] rounded-2xl bg-[#0066CC] text-white px-4 py-3 text-sm">
                          {scenario.guest2}
                        </div>
                      </div>
                    )}
                    {scenario.ai2 && (
                      <div className="flex justify-start">
                        <div className="max-w-[85%] rounded-2xl bg-white border border-[#E5E7EB] px-4 py-3 text-sm text-[#2B3A4D]">
                          {scenario.ai2}
                        </div>
                      </div>
                    )}
                    {scenario.guest3 && (
                      <div className="flex justify-end">
                        <div className="max-w-[85%] rounded-2xl bg-[#0066CC] text-white px-4 py-3 text-sm">
                          {scenario.guest3}
                        </div>
                      </div>
                    )}
                    {scenario.ai3 && (
                      <div className="flex justify-start">
                        <div className="max-w-[85%] rounded-2xl bg-white border border-[#E5E7EB] px-4 py-3 text-sm text-[#2B3A4D]">
                          {scenario.ai3}
                        </div>
                      </div>
                    )}
                  </div>
                </ScrollReveal>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* SECTION 6: Integration */}
      <section className="relative py-24 bg-[#F8F9FA] overflow-hidden">
        {images.aiConcierge?.integration && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-[0.04]"
            style={{ backgroundImage: `url(${images.aiConcierge.integration})`, filter: 'grayscale(1)' }}
          />
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2B3A4D] mb-4 text-center">
              {t('aiConcierge.integration.title')}
            </h2>
            <p className="text-[#6B7B8C] text-lg text-center mb-16">
              {t('aiConcierge.integration.subtitle')}
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t('aiConcierge.integration.categories').map((cat, index) => (
              <ScrollReveal key={index} delay={index * 80}>
                <Card className="bg-white border-0 shadow-sm hover:shadow-lg hover:border-[#0066CC]/20 transition-all duration-300 h-full perspective-container card-3d">
                  <CardHeader>
                    <CardTitle className="text-lg text-[#0066CC]">
                      {cat.category}
                    </CardTitle>
                    <p className="text-sm text-[#6B7B8C] font-medium">
                      {cat.platforms}
                    </p>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-[#5C5C5C] text-sm">{cat.whatAiDoes}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7: Guest Journey */}
      <section className="relative py-24 bg-white overflow-hidden">
        {images.aiConcierge?.journey && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-[0.05]"
            style={{ backgroundImage: `url(${images.aiConcierge.journey})`, filter: 'grayscale(1)' }}
          />
        )}
        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2B3A4D] mb-16 text-center">
              {t('aiConcierge.guestJourney.title')}
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t('aiConcierge.guestJourney.phases').map((phase, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="relative group">
                  {index < 3 && (
                    <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-[#0066CC]/20 -translate-y-1/2 transition-colors duration-300 group-hover:bg-[#0066CC]/40" />
                  )}
                  <div className="flex flex-col items-center text-center transition-transform duration-300 group-hover:scale-[1.02]">
                    <div className="w-16 h-16 rounded-full bg-[#0066CC]/10 flex items-center justify-center mb-4 transition-all duration-300 group-hover:bg-[#0066CC]/20 group-hover:shadow-lg group-hover:shadow-[#0066CC]/20">
                      <span className="text-[#0066CC] font-bold">
                        {index + 1}
                      </span>
                    </div>
                    <h3 className="font-semibold text-[#2B3A4D] mb-2">
                      {phase.title}
                    </h3>
                    <p className="text-sm text-[#6B7B8C] leading-relaxed">
                      {phase.points}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8: Implementation Timeline */}
      <section className="relative py-24 bg-[#F8F9FA] overflow-hidden">
        {images.aiConcierge?.journey && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-[0.03]"
            style={{ backgroundImage: `url(${images.aiConcierge.journey})`, filter: 'grayscale(1)' }}
          />
        )}
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2B3A4D] mb-16 text-center">
              {t('aiConcierge.implementation.title')}
            </h2>
          </ScrollReveal>

          <div className="space-y-6">
            {t('aiConcierge.implementation.phases').map((phase, index) => (
              <ScrollReveal key={index} delay={index * 80}>
                <div className="flex flex-col md:flex-row md:items-center gap-4 p-6 rounded-xl bg-white shadow-sm border border-[#E5E7EB] transition-all duration-300 hover:shadow-md hover:border-[#0066CC]/20 hover:-translate-y-0.5">
                  <div className="md:w-32 flex-shrink-0">
                    <span className="inline-block px-3 py-1 rounded-full bg-[#0066CC]/10 text-[#0066CC] font-medium text-sm">
                      {phase.duration}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#2B3A4D] mb-1">
                      {phase.phase}
                    </h3>
                    <p className="text-[#6B7B8C] text-sm">{phase.description}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9: Security */}
      <section className="relative py-24 bg-white overflow-hidden">
        {images.aiConcierge?.security && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-[0.04]"
            style={{ backgroundImage: `url(${images.aiConcierge.security})`, filter: 'grayscale(1)' }}
          />
        )}
        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2B3A4D] mb-4 text-center">
              {t('aiConcierge.security.title')}
            </h2>
            <p className="text-[#6B7B8C] text-lg text-center mb-16">
              {t('aiConcierge.security.subtitle')}
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t('aiConcierge.security.columns').map((col, index) => {
              const icons = [Lock, Server, Scale];
              const Icon = icons[index];
              return (
                <ScrollReveal key={index} delay={index * 100}>
                  <Card className="group bg-[#F8F9FA] border-0 text-center p-8 h-full transition-all duration-300 hover:shadow-lg hover:bg-white hover:border-[#0066CC]/10 perspective-container card-3d">
                    <div className="w-14 h-14 rounded-full bg-[#0066CC]/10 flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110">
                      <Icon className="w-7 h-7 text-[#0066CC]" />
                    </div>
                    <CardTitle className="text-lg text-[#2B3A4D] mb-3">
                      {col.title}
                    </CardTitle>
                    <p className="text-sm text-[#6B7B8C] leading-relaxed">
                      {col.points}
                    </p>
                  </Card>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 10: CTA */}
      <section className="relative py-24 bg-[#2B3A4D] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage: images.patterns?.geometric
              ? `url(${images.patterns.geometric})`
              : 'none',
          }}
        />
        <AnimatedBackground className="opacity-30" />
        <ScrollReveal>
          <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('aiConcierge.cta.headline')}
            </h2>
            <p className="text-white/70 text-lg mb-8">
              {t('aiConcierge.cta.subtitle')}
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#0066CC] hover:bg-[#0055AA] text-white px-10 py-6 text-base"
              >
                {t('aiConcierge.cta.button')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <p className="text-white/60 text-sm mt-6">
              <a
                href="mailto:contact@hitech-group.io"
                className="hover:text-white transition-colors"
              >
                {t('aiConcierge.cta.secondaryLink')}
              </a>
            </p>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
};

export default AiConciergePage;
